/**
 * SAMURAI TCG - Collection Page Module
 * Sidebar filter layout — all filters are server-side.
 *
 * Shopify paginate max = 50/page.
 * For 100/200 per page, JS fetches additional Shopify pages in parallel.
 * All filtering (rarity, set, price, availability) is server-side via URL.
 * JS handles: pagination, sort, per-page, card size, set list search, UI interactions.
 */

(function () {
  'use strict';

  const TCGCollection = {
    _currentSize: 'm',
    _perPage: 50,
    _shopifyPage: 1,
    _shopifyTotalPages: 1,
    _totalProducts: 0,

    init() {
      this.bindEvents();
      this.initFromURL();
      this.initPagination();
      this.initPriceFilterServer();
      this.initSidebarToggles();
      this.initSetListSearch();
      this.initProductTypeFilter();
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        const sizeBtn = e.target.closest('[data-tcg-card-size]');
        if (sizeBtn) this.setCardSize(sizeBtn.dataset.tcgCardSize, sizeBtn);

        const cardImage = e.target.closest('[data-tcg-image-zoom]');
        if (cardImage) this.openImageModal(cardImage);

        if (e.target.closest('.tcg-image-modal')) this.closeImageModal();
      });

      // Back to top
      window.addEventListener('scroll', () => {
        const btn = document.getElementById('tcg-back-to-top');
        if (btn) btn.classList.toggle('is-visible', window.scrollY > 600);
      });

      const backToTopBtn = document.getElementById('tcg-back-to-top');
      if (backToTopBtn) {
        backToTopBtn.addEventListener('click', () => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }

      // Sort
      const sortSelect = document.querySelector('[data-tcg-sort]');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => this.applySort(e.target.value));
      }

      // Per page
      const perPageSelect = document.querySelector('[data-tcg-per-page]');
      if (perPageSelect) {
        perPageSelect.addEventListener('change', (e) => {
          const url = new URL(window.location.href);
          url.searchParams.set('per_page', e.target.value);
          url.searchParams.delete('page');
          window.location.href = url.toString();
        });
      }

      // Mobile filter sidebar
      const filterToggle = document.getElementById('tcg-filter-toggle');
      const sidebar = document.getElementById('tcg-sidebar');
      const sidebarClose = document.getElementById('tcg-sidebar-close');
      if (filterToggle && sidebar) {
        filterToggle.addEventListener('click', () => {
          sidebar.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });
      }
      if (sidebarClose && sidebar) {
        sidebarClose.addEventListener('click', () => {
          sidebar.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      }

      // Close sidebar on overlay click
      document.addEventListener('click', (e) => {
        if (sidebar && sidebar.classList.contains('is-open')) {
          if (!sidebar.contains(e.target) && !e.target.closest('#tcg-filter-toggle')) {
            sidebar.classList.remove('is-open');
            document.body.style.overflow = '';
          }
        }
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeImageModal();
          if (sidebar) {
            sidebar.classList.remove('is-open');
            document.body.style.overflow = '';
          }
        }
      });
    },

    initFromURL() {
      const params = new URLSearchParams(window.location.search);

      const size = params.get('size');
      if (size && ['s', 'm', 'l'].includes(size)) {
        const btn = document.querySelector(`[data-tcg-card-size="${size}"]`);
        if (btn) this.setCardSize(size, btn);
      }

      const sortBy = params.get('sort_by');
      if (sortBy) {
        const sortSelect = document.querySelector('[data-tcg-sort]');
        if (sortSelect) sortSelect.value = sortBy;
      }

      // Restore price inputs from the URL so a reload or Back keeps the visible filter in sync.
      for (const [param, id] of [
        ['filter.v.price.gte', 'tcg-price-gte'],
        ['filter.v.price.lte', 'tcg-price-lte'],
      ]) {
        const value = params.get(param);
        const input = document.getElementById(id);
        if (input && value !== null && value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0) {
          input.value = value;
        }
      }

      this._perPage = parseInt(params.get('per_page'), 10) || 50;
      if (![50, 100, 200].includes(this._perPage)) this._perPage = 50;
      const perPageSelect = document.querySelector('[data-tcg-per-page]');
      if (perPageSelect) perPageSelect.value = this._perPage;
    },

    // ─── Sidebar Toggles ──────────────────────────────

    initSidebarToggles() {
      document.querySelectorAll('[data-tcg-sidebar-toggle]').forEach((header) => {
        header.addEventListener('click', () => {
          const body = header.nextElementSibling;
          if (body) body.classList.toggle('is-open');
        });
      });
    },

    // ─── Set List Search (filter links by text, not products) ───

    initSetListSearch() {
      const searchInput = document.querySelector('[data-tcg-set-list-search]');
      if (!searchInput) return;

      let debounceTimer;
      searchInput.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const query = searchInput.value.trim().toLowerCase();
          document.querySelectorAll('.tcg-sidebar__set-link').forEach((link) => {
            const name = link.dataset.setName || '';
            link.style.display = (!query || name.includes(query)) ? '' : 'none';
          });
        }, 150);
      });
    },

    // ─── Product Type Filter (client-side fallback) ───

    initProductTypeFilter() {
      const buttons = document.querySelectorAll('[data-tcg-ptype-filter]');
      if (buttons.length === 0) return;

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const filterVal = btn.dataset.tcgPtypeFilter;
          buttons.forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          const cards = document.querySelectorAll('.tcg-card');
          let visibleCount = 0;
          cards.forEach((card) => {
            if (!filterVal || card.dataset.productType === filterVal) {
              card.style.display = '';
              visibleCount++;
            } else {
              card.style.display = 'none';
            }
          });

          // Update count in pagination info
          const info = document.querySelector('.tcg-pagination__info');
          if (info && filterVal) {
            info.textContent = visibleCount + ' items shown';
          }
        });
      });
    },

    // ─── Card Size ────────────────────────────────────

    setCardSize(size, btn) {
      this._currentSize = size;
      document.querySelectorAll('[data-tcg-card-size]').forEach((b) => {
        b.classList.remove('is-active');
      });
      if (btn) btn.classList.add('is-active');

      document.querySelectorAll('.tcg-card-grid').forEach((grid) => {
        grid.classList.remove('tcg-card-grid--s', 'tcg-card-grid--l');
        if (size === 's') grid.classList.add('tcg-card-grid--s');
        if (size === 'l') grid.classList.add('tcg-card-grid--l');
      });
    },

    // ─── Image Modal ──────────────────────────────────

    openImageModal(el) {
      const img = el.querySelector('img');
      if (!img) return;
      const modal = document.getElementById('tcg-image-modal');
      const modalImg = document.getElementById('tcg-image-modal-img');
      if (!modal || !modalImg) return;
      modalImg.src = img.dataset.fullSrc || img.src;
      modalImg.alt = img.alt;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    closeImageModal() {
      const modal = document.getElementById('tcg-image-modal');
      if (modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    },

    // ─── Sort ─────────────────────────────────────────

    applySort(sortValue) {
      const url = new URL(window.location.href);
      if (sortValue === 'manual') {
        url.searchParams.delete('sort_by');
      } else {
        url.searchParams.set('sort_by', sortValue);
      }
      url.searchParams.delete('page');
      window.location.href = url.toString();
    },

    // ─── Price Filter (Server-side via Storefront Filtering API) ───

    initPriceFilterServer() {
      const applyBtn = document.getElementById('tcg-price-apply');
      const resetBtn = document.getElementById('tcg-price-reset-server');
      const gteInput = document.getElementById('tcg-price-gte');
      const lteInput = document.getElementById('tcg-price-lte');

      if (!applyBtn) return;

      const navigate = () => {
        const container = document.querySelector('[data-tcg-price-filter]');
        const baseUrl = container ? container.dataset.collectionUrl : window.location.pathname;
        const url = new URL(baseUrl, window.location.origin);

        // Preserve existing non-price params (including availability)
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((val, key) => {
          if (!key.startsWith('filter.v.price')) {
            url.searchParams.append(key, val);
          }
        });

        // Set price filters
        const gte = gteInput ? gteInput.value.trim() : '';
        const lte = lteInput ? lteInput.value.trim() : '';
        if (gte) url.searchParams.set('filter.v.price.gte', gte);
        if (lte) url.searchParams.set('filter.v.price.lte', lte);

        // Reset pagination
        url.searchParams.delete('page');

        window.location.href = url.toString();
      };

      applyBtn.addEventListener('click', navigate);

      // Enter key submits
      [gteInput, lteInput].forEach((input) => {
        if (input) {
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') navigate();
          });
        }
      });

      // Reset
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          const container = document.querySelector('[data-tcg-price-filter]');
          const baseUrl = container ? container.dataset.collectionUrl : window.location.pathname;
          const url = new URL(baseUrl, window.location.origin);

          const currentParams = new URLSearchParams(window.location.search);
          currentParams.forEach((val, key) => {
            if (!key.startsWith('filter.v.price')) {
              url.searchParams.append(key, val);
            }
          });
          url.searchParams.delete('page');

          window.location.href = url.toString();
        });
      }
    },

    // ─── Pagination ───────────────────────────────────

    initPagination() {
      const main = document.getElementById('tcg-collection-main');
      if (!main) return;

      this._shopifyPage = parseInt(main.dataset.paginationCurrent, 10) || 1;
      this._shopifyTotalPages = parseInt(main.dataset.paginationTotal, 10) || 1;
      this._totalProducts = parseInt(main.dataset.productsTotal, 10) || 0;

      const shopifyPagesPerVP = this._perPage / 50;

      if (shopifyPagesPerVP > 1) {
        const expectedStart =
          (Math.ceil(this._shopifyPage / shopifyPagesPerVP) - 1) * shopifyPagesPerVP + 1;
        if (this._shopifyPage !== expectedStart) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', expectedStart);
          window.location.replace(url.toString());
          return;
        }
      }

      if (shopifyPagesPerVP > 1) {
        this.loadAdditionalPages(shopifyPagesPerVP - 1);
      } else {
        this.renderPagination();
      }
    },

    async loadAdditionalPages(count) {
      const loading = document.getElementById('tcg-page-loading');
      if (loading) loading.style.display = '';

      const promises = [];
      for (let i = 1; i <= count; i++) {
        const targetPage = this._shopifyPage + i;
        if (targetPage > this._shopifyTotalPages) break;

        const url = new URL(window.location.href);
        url.searchParams.set('page', targetPage);
        url.searchParams.set('section_id', 'tcg-collection-product-grid');

        promises.push(
          fetch(url.toString())
            .then((r) => (r.ok ? r.text() : Promise.reject('HTTP ' + r.status)))
            .then((html) => ({ page: targetPage, html }))
            .catch((err) => {
              console.error('Failed to load page', targetPage, err);
              return null;
            })
        );
      }

      const results = await Promise.all(promises);

      results
        .filter((r) => r !== null)
        .sort((a, b) => a.page - b.page)
        .forEach((r) => this.mergeProducts(r.html));

      if (loading) loading.style.display = 'none';

      this.renderPagination();
      this.refreshWishlist();
    },

    // Wishlist Club only scans markers present at load; initialize the hearts of appended cards.
    refreshWishlist(attempt = 0) {
      if (!window.tcgWishlistEnabled) return;
      clearTimeout(this._wishlistRefreshTimer);
      const grid = document.getElementById('tcg-card-grid');
      if (!grid || !grid.querySelector('.th_wl_col_btn:not(:has(.wc_wishlistBlock))')) return;
      if (typeof window.wc_get_product_wishlist === 'function') {
        Promise.resolve(window.wc_get_product_wishlist()).catch((error) => {
          console.warn('Wishlist initialization failed', error);
        });
      }
      // Loading the vendor bundle and fetching appended products are asynchronous.
      if (attempt < 20) {
        this._wishlistRefreshTimer = setTimeout(() => this.refreshWishlist(attempt + 1), 500);
      } else {
        console.warn('Wishlist buttons remain uninitialized');
      }
    },

    mergeProducts(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const grid = document.getElementById('tcg-card-grid');
      if (!grid) return;

      const newGrid = doc.getElementById('tcg-card-grid');
      if (!newGrid) return;

      const newCards = newGrid.querySelectorAll('.tcg-card');
      newCards.forEach((card) => {
        grid.appendChild(document.importNode(card, true));
      });

      grid.classList.remove('tcg-card-grid--s', 'tcg-card-grid--l');
      if (this._currentSize === 's') grid.classList.add('tcg-card-grid--s');
      if (this._currentSize === 'l') grid.classList.add('tcg-card-grid--l');
    },

    renderPagination() {
      const container = document.getElementById('tcg-pagination');
      if (!container) return;

      const shopifyPagesPerVP = this._perPage / 50;
      const totalVP = Math.ceil(this._shopifyTotalPages / shopifyPagesPerVP);
      const currentVP = Math.ceil(this._shopifyPage / shopifyPagesPerVP);

      if (totalVP <= 1) {
        container.style.display = 'none';
        return;
      }

      container.style.display = '';
      let html = '';

      if (currentVP > 1) {
        const prevStart = (currentVP - 2) * shopifyPagesPerVP + 1;
        html += '<a href="' + this.buildPageUrl(prevStart) + '" class="tcg-pagination__btn">‹ Prev</a>';
      } else {
        html += '<span class="tcg-pagination__btn is-disabled">‹ Prev</span>';
      }

      const pages = this.getPaginationNumbers(currentVP, totalVP);
      for (const p of pages) {
        if (p === '...') {
          html += '<span class="tcg-pagination__num is-gap">…</span>';
        } else if (p === currentVP) {
          html += '<span class="tcg-pagination__num is-current">' + p + '</span>';
        } else {
          const start = (p - 1) * shopifyPagesPerVP + 1;
          html += '<a href="' + this.buildPageUrl(start) + '" class="tcg-pagination__num">' + p + '</a>';
        }
      }

      if (currentVP < totalVP) {
        const nextStart = currentVP * shopifyPagesPerVP + 1;
        html += '<a href="' + this.buildPageUrl(nextStart) + '" class="tcg-pagination__btn">Next ›</a>';
      } else {
        html += '<span class="tcg-pagination__btn is-disabled">Next ›</span>';
      }

      const startItem = (currentVP - 1) * this._perPage + 1;
      const endItem = Math.min(currentVP * this._perPage, this._totalProducts);
      html +=
        '<div class="tcg-pagination__info">' + startItem + '–' + endItem + ' of ' + this._totalProducts + '</div>';

      container.innerHTML = html;
    },

    getPaginationNumbers(current, total) {
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      const pages = [];
      pages.push(1);
      if (current > 3) pages.push('...');
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
        pages.push(i);
      }
      if (current < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    },

    buildPageUrl(shopifyPage) {
      const url = new URL(window.location.href);
      if (shopifyPage <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', shopifyPage);
      }
      if (this._perPage !== 50) {
        url.searchParams.set('per_page', this._perPage);
      } else {
        url.searchParams.delete('per_page');
      }
      return url.toString();
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TCGCollection.init());
  } else {
    TCGCollection.init();
  }

  window.TCGCollection = TCGCollection;
})();
